import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../lib/session';
import type { IncidentKind, Member } from '../lib/contract';
import { apiFetch, useResourceMapped } from '../lib/api';
import { mapPortfolio, type ApiPortfolio } from '../lib/adapt';
import type { Portfolio } from '../lib/contract';
import { useMembers } from '../lib/members';
import { Btn, Card, Field } from '../components/ui/primitives';
import { UserCombo } from '../components/ui/UserCombo';

const USE_FIXTURES = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';

export function NewIncident() {
  const { session } = useSession();
  const navigate = useNavigate();
  const { members } = useMembers(session.organizationId);
  const [kind, setKind] = useState<IncidentKind>('vulnerability');
  // Default assignee = the creator; live mode falls back to a session-derived
  // member while the org roster loads. Fixtures keep the prior null-if-no-match.
  const defaultAssignee: Member | null = members.find((m) => m.id === session.userRef)
    ?? (USE_FIXTURES ? null : { id: session.userRef, name: session.userName, role: session.role === 'manager' ? 'Manager' : 'User' });
  const [assignee, setAssignee] = useState<Member | null>(defaultAssignee);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [malicious, setMalicious] = useState<'' | 'yes' | 'no'>(''); // '' = unknown / still triaging
  const [cve, setCve] = useState('');
  const [component, setComponent] = useState('Gateway GW-200 v3.4');
  // Live mode: the event anchors to a real product VERSION (backend requires versionId).
  const portfolio = useResourceMapped<ApiPortfolio, Portfolio>(session, USE_FIXTURES ? null : '/api/portfolio', mapPortfolio);
  const versionOptions = (portfolio.data?.products ?? []).flatMap((p) =>
    p.versions.map((v) => ({ id: v.id, label: `${p.name} ${v.label}` })),
  );
  const [versionId, setVersionId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (USE_FIXTURES) { navigate('/app/incidents/INC-104'); return; }
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(session, '/api/art14', {
        method: 'POST',
        body: JSON.stringify({
          kind,
          title: title.trim() || undefined,
          cveId: cve.trim() || undefined,
          versionId,
          // The reporter's EXPLICIT assignee — persisted, not discarded (R-INC-01).
          assigneeUserRef: assignee?.id,
          description: description.trim() || undefined,
          // Q16 i13 (severe incident only) — omit while still triaging.
          ...(kind === 'incident' && malicious ? { suspectedMalicious: malicious === 'yes' } : {}),
          detectedAt: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error(`create failed (${res.status})`);
      const { id } = (await res.json()) as { id: string };
      navigate(`/app/incidents/${id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <>
      <div>
        <div className="crumbs"><Link to="/app/incidents">Incidents</Link> ／ New incident</div>
        <h1 style={{ fontSize: 'calc(27px*var(--fs))' }}>New incident</h1>
      </div>
      <Card style={{ padding: 24 }}>
        <div className="form">
          <Field label="Incident type" req
            hint={kind === 'incident'
              ? 'Severe incidents follow their own track: 24h → 72h → final report at 1 month.'
              : 'KEV-triggered Art.14 vulnerability events open here automatically.'}>
            <select className="select" value={kind} onChange={(e) => setKind(e.target.value as IncidentKind)}>
              <option value="vulnerability">Vulnerability (manual)</option>
              <option value="incident">Severe incident</option>
            </select>
          </Field>

          <Field label="Title" req>
            <input className="input" placeholder="Short summary of the issue" data-testid="incident-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>

          {/* Every incident MUST be assigned (R-INC-01) — mandatory at creation. */}
          <Field label="Assign to" req hint="An incident is never left unassigned — defaults to you; the org default Incident Manager is the fallback.">
            <UserCombo members={members} value={assignee} onPick={setAssignee} />
          </Field>

          <Field label="Affected product / version" req
            hint="Every Art.14 event anchors to a real product version — its markets, SBOM and deadlines hang off it.">
            {USE_FIXTURES ? (
              <select className="select" value={component} onChange={(e) => setComponent(e.target.value)}>
                <option>Gateway GW-200 v3.4</option>
                <option>Pump Controller PC-90 v1.2</option>
                <option>Relay Node RN-7 v1.5</option>
              </select>
            ) : versionOptions.length === 0 ? (
              <div className="hint" style={{ padding: '10px 12px', border: '1px dashed var(--border)', borderRadius: 8 }}>
                No product versions yet — <Link to="/app/products/new" style={{ color: 'var(--teal)' }}>create a product</Link> and
                upload an SBOM first, then open the incident against that version.
              </div>
            ) : (
              <select className="select" value={versionId} onChange={(e) => setVersionId(e.target.value)}>
                <option value="">— pick the affected version —</option>
                {versionOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            )}
          </Field>

          <details className="more">
            <summary>More details</summary>
            <div className="inner">
              <div className="grid2">
                <Field label="CVE (optional)"><input className="input" placeholder="CVE-YYYY-NNNNN" value={cve} onChange={(e) => setCve(e.target.value)} /></Field>
              </div>
              {kind === 'incident' && (
                <Field label="Suspected unlawful or malicious acts" hint="Art.14(4)(a) — the one extra thing a severe-incident 24h warning must state (Q16 i13).">
                  <select className="select" data-testid="incident-malicious" value={malicious} onChange={(e) => setMalicious(e.target.value as '' | 'yes' | 'no')}>
                    <option value="">Unknown — still triaging</option>
                    <option value="yes">Yes — suspected</option>
                    <option value="no">No</option>
                  </select>
                </Field>
              )}
              <Field label="Description"><textarea placeholder="What's affected, how it was found…" data-testid="incident-description" value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
            </div>
          </details>

          <div className="form-actions">
            {error && <div className="hint" style={{ color: 'var(--danger, #c0392b)' }}>{error}</div>}
            <Btn onClick={() => navigate('/app/incidents')}>Cancel</Btn>
            {!USE_FIXTURES && !versionId && <span className="hint">pick the affected version to enable</span>}
            <Btn variant="primary" disabled={!title.trim() || !assignee || busy || (!USE_FIXTURES && !versionId)} onClick={() => void create()}>Create incident</Btn>
          </div>
        </div>
      </Card>
    </>
  );
}
